import InfiniteScroll from '@/components/common/InfiniteScroll';
import ProductCard from '@/components/common/ProductCard/Vertical';
import { CmsComponentProps } from '@/types/cms';
import { FC, useEffect, useState, useRef, useCallback } from 'react';
import Masonry from 'react-masonry-css';
import { getHomePageDataByUrl } from '@/api/content';
import { getDeliveryDate } from '@/utils/common/porder';
import { getConfigBg } from '@/components/component-pool/tools/config-bg';
import { AddToCartReferType } from '@/constants/track';
import SwiperBanner from '../../units/SwiperBanner';
import StoreBanner from '../../units/StoreBanner';
import VideoBanner from '../../units/VideoBanner';
import { v4 as uuid } from 'uuid';
import Container from '../Layout/container';
import CategoryFilter from '../../units/CategoryFilter';
import ContentSkeleton from './content-skeleton';
import dynamic from 'next/dynamic';
import Header from '../Layout/header';
import { useComponentTheme } from '../../hooks/useComponentTheme';
import useAccount from '@/store/account';
import { isValidArray } from '@/utils/isValidArray';
import { isMaxLimitPurchased } from '@/utils/productFormat';
import { twMerge } from 'tailwind-merge';

const Empty = dynamic(() => import('./empty'), {
  ssr: false
});

type CategoryFilterConfig = {
  // category filter 吸顶的高度
  stickyTop?: number;
  // category filter 判断entry的rootMargin
  scrollHeight?: number;
  // category 是否吸顶
  stickyFilter?: boolean;
  // 点击category item时，页面reset的位置距离
  resetDistance?: number;
};

type Props = CmsComponentProps & CategoryFilterConfig;

const constructFilterData = (oldData: any[], newData: any[], key: string) => {
  if (!key || !isValidArray(oldData)) return newData;
  const newFilter = oldData.map((item: any) => {
    return {
      ...item,
      selected: item.key === key
    };
  });
  return newFilter;
};

const ContentFeed: FC<Props> = props => {
  const {
    properties,
    postUrl,
    cmsComponentKey,
    isVendorPage,
    skeleton,
    componentPos,
    trackingContext,
    addCartParams,
    componentDataSourceKey,
    customAddCartApi,
    stickyTop,
    scrollHeight,
    stickyFilter,
    resetDistance = 0,
    pageTheme,
    className
  } = props;

  const { titleStyle, moreLinkStyle, pageTheme: parsedPageTheme } = useComponentTheme({ properties, pageTheme });

  const [load, setLoad] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [filter, setFilter] = useState<any>([]);
  const categoryRef = useRef<string>('feature');
  const randomRef = useRef(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const canQuery = useRef<boolean>(true);
  const [pageNum, setPageNum] = useState<number>(1);
  const isPlayIdRef = useRef('');

  const eventKey = properties?.event_key || cmsComponentKey;
  const { userBoughtData } = useAccount();

  const bgStyle = getConfigBg(properties?.background);

  const fetchData = async (key = '', cb?) => {
    if (!canQuery?.current) return;
    categoryRef.current = key;
    const res = await getHomePageDataByUrl(
      postUrl,
      {
        recommend_session: randomRef.current,
        page_num: pageNum,
        key: key,
        dataobject_key: componentDataSourceKey,
        from_page: 'global'
      },
      undefined,
      {
        cache: true,
        namespace: cmsComponentKey
      }
    );
    canQuery.current = true;
    if (res?.result && categoryRef.current === key) {
      if (res?.object?.total_count <= 10) {
        setHasMore(false);
      }

      setLoad(false);
      setContentLoading(false);
      setData(res?.object?.contents || []);
      setFilter(pre => {
        const newFilter = constructFilterData(pre, res?.object?.tabs || [], key);
        return newFilter;
      });
      setPageNum(pre => pre + 1);
      cb?.();
    }
  };

  const loadMoreData = async () => {
    if (!hasMore) return;
    if (!canQuery?.current) return;
    canQuery.current = false;
    const res = await getHomePageDataByUrl(
      postUrl,
      {
        recommend_session: randomRef.current,
        page_num: pageNum,
        key: categoryRef.current,
        dataobject_key: componentDataSourceKey,
        from_page: 'global'
      },
      undefined,
      {
        cache: true,
        namespace: cmsComponentKey
      }
    );
    canQuery.current = true;
    if (res?.result) {
      const _data = res?.object?.contents || [];
      setPageNum(pre => pre + 1);
      if (!_data?.length) setHasMore(false);
      setData(pre => [...pre, ..._data]);
    }
  };

  useEffect(() => {
    randomRef.current = uuid();
    fetchData(categoryRef.current);
    return () => {
      window[cmsComponentKey] = null;
    };
  }, []);

  const handleChangeTab = useCallback(
    (key, cb) => {
      if (!canQuery?.current) return;
      setContentLoading(true);
      const dis = document.getElementById(`feedContent`)?.offsetTop;
      dis && window.scrollTo({ top: dis - resetDistance, behavior: 'smooth' });
      setPageNum(1);
      setHasMore(true);
      cb?.();
      fetchData(key);
    },
    [resetDistance]
  );

  /** 列表页视频播放（自动播放，手动播放）  */
  const videoTimer = useRef(null);
  const currentVideoInView = useRef(null);
  const playingVideoProps = useRef(null);
  const inViewVideoMap = {};

  const handleInViewChange = props => {
    const { inView, videoInfo, play, stop } = props;
    if (inView) {
      inViewVideoMap[videoInfo.id] = props;
    } else {
      delete inViewVideoMap[videoInfo.id];
    }

    if (isPlayIdRef.current === '' && inView) {
      playingVideoProps.current = props;
      currentVideoInView.current = null;
      isPlayIdRef.current = `${videoInfo.id}`;
      play?.();
      return;
    }
    if (isPlayIdRef.current === `${videoInfo.id}`) {
      currentVideoInView.current = inView;
      if (!inView) {
        clearTimeout(videoTimer.current);
        videoTimer.current = setTimeout(() => {
          if (currentVideoInView.current === false) {
            isPlayIdRef.current = '';
            playingVideoProps.current = null;
            stop?.();
            if (Object.keys(inViewVideoMap).length) {
              for (const key in inViewVideoMap) {
                const rect = inViewVideoMap[key].cardElement.getBoundingClientRect();
                if (rect.top < rect.height || rect.bottom < rect.height) continue; // 跳过大部分内容被遮住的元素
                playingVideoProps.current = props;
                isPlayIdRef.current = `${key}`;
                inViewVideoMap[key].play?.();
                return;
              }
            } else return;
          }
        }, 300);
      }
    }
  };

  if (load) {
    return (
      <Container cmsComponentKey={cmsComponentKey} className={twMerge('pb-4', className)} style={{ ...bgStyle }}>
        {!!properties?.title && (
          <Header
            cmsComponentKey={props.cmsComponentKey}
            title={properties.title}
            titleStyle={titleStyle}
            moreLinkStyle={moreLinkStyle}
            className="mb-2.5"
          />
        )}
        <div>{skeleton || <ContentSkeleton />}</div>
      </Container>
    );
  }

  if (!load && !data?.length && !filter?.length) return null;

  return (
    <Container cmsComponentKey={cmsComponentKey} className={twMerge('pb-4', className)} style={{ ...bgStyle }}>
      {!!properties?.title && (
        <Header
          cmsComponentKey={props.cmsComponentKey}
          title={properties.title}
          titleStyle={titleStyle}
          moreLinkStyle={moreLinkStyle}
          className="mb-2.5"
        />
      )}
      <CategoryFilter
        componentPos={componentPos}
        filterData={filter}
        onClickTab={handleChangeTab}
        stickyTop={stickyTop}
        scrollHeight={scrollHeight}
        stickyFilter={stickyFilter}
        pageTheme={parsedPageTheme}
        testIdPrefix={`wid-collection-${cmsComponentKey}-filter`}
      />
      <div id="feedContent">
        {contentLoading ? (
          <ContentSkeleton />
        ) : data?.length > 0 ? (
          <InfiniteScroll hasMore={hasMore} loader={null} loadMore={loadMoreData}>
            <Masonry className="flex w-auto pl-0 pr-2.5 py-0 -mt-1" columnClassName="bg-clip-padding box-border pl-2.5">
              {data?.map((item, index) => {
                if (item.type === 'banners')
                  return (
                    <div
                      className="mt-5"
                      key={`${item.type}-${index}`}
                      data-testid={`wid-collection-${cmsComponentKey}-${item.type}-container`}>
                      <SwiperBanner
                        testIdPrefix={`wid-collection-${cmsComponentKey}-${item.type}`}
                        data={item.data.banners.carousel}
                        type={item?.type}
                        {...props}
                        trackingContext={{
                          filter_sub_category: categoryRef.current,
                          ...trackingContext
                        }}
                      />
                    </div>
                  );
                if (item.type === 'video')
                  return (
                    <div className="mt-5" key={`${item.type}-${index}`}>
                      <VideoBanner
                        data={item.data.post}
                        type={item?.type}
                        onViewChange={handleInViewChange}
                        index={index}
                        trackingContext={{
                          filter_sub_category: categoryRef.current,
                          ...trackingContext
                        }}
                        {...props}
                      />
                    </div>
                  );
                if (item.type === 'seller')
                  return (
                    <div
                      className="mt-5"
                      key={`${item.type}-${index}`}
                      data-testid={`wid-collection-${cmsComponentKey}-${item.type}-container`}>
                      <StoreBanner
                        data={item.data.seller}
                        type={item?.type}
                        index={index}
                        {...props}
                        trackingContext={{
                          filter_sub_category: categoryRef.current,
                          ...trackingContext
                        }}
                      />
                    </div>
                  );
                if (item.type === 'item') {
                  const _productItem = item?.data?.product;
                  if (props?.filterMaxPurchasedCard) {
                    const isMaxLimit = isMaxLimitPurchased(_productItem, userBoughtData);
                    if (isMaxLimit) {
                      return null;
                    }
                  }
                  return (
                    <div className="mt-5 rounded-500 overflow-hidden" key={`${item.type}-${index}`}>
                      <ProductCard
                        addCartParams={{
                          referType: AddToCartReferType.seller,
                          deliveryDate: getDeliveryDate(),
                          ...((addCartParams || {}) as any)
                        }}
                        positionInfoT2={{
                          modSecPos: {
                            mod_nm: `${eventKey}`,
                            mod_pos: componentPos
                          },
                          prodPos: index,
                          context: {
                            filter_sub_category: categoryRef.current,
                            global_vendor: _productItem?.biz_type === 'seller' ? _productItem?.vender_id : null,
                            ...trackingContext
                          }
                        }}
                        showWeekly
                        showAllTags={true}
                        showSoldBy={!isVendorPage}
                        data={_productItem}
                        customAddCartApi={customAddCartApi}
                      />
                    </div>
                  );
                }
              })}
            </Masonry>
          </InfiniteScroll>
        ) : (
          <Empty />
        )}
      </div>
    </Container>
  );
};

export default ContentFeed;
